// What a voxel is made of, for surface texturing.
//
// The unit shader samples a tileable material atlas (`scripts/generate-unit-atlas.mjs`) by a
// per-vertex material class. A prefab never says what its voxels are made of, so the class is
// read off the voxel: its metalness, glow and its colour (hue, saturation, lightness) and
// roughness. It is a heuristic with a few clear cases (metal, bone, skin, stone, green hide),
// cloth as the default, and `0` — no texture — for glowing voxels.
import type { BlockVoxel } from './types.js';

export const MATERIAL_NONE = 0;
export const MATERIAL_CLOTH = 1;
export const MATERIAL_LEATHER = 2;
export const MATERIAL_METAL = 3;
export const MATERIAL_STONE = 4;
export const MATERIAL_BONE = 5;
export const MATERIAL_SKIN = 6;
export const MATERIAL_HIDE = 7;
export const MATERIAL_WOOD = 8;

/** Hue 0..360, saturation and lightness 0..1 of a #rrggbb colour. */
function hsl(hex: string): { h: number; s: number; l: number } {
  const n = Number.parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: (h * 60 + 360) % 360, s, l };
}

/** The material class of a voxel (one of the `MATERIAL_*` constants). */
export function materialClassOf(
  v: Pick<BlockVoxel, 'color' | 'metalness' | 'roughness' | 'emissive' | 'team'>,
): number {
  if (v.emissive) return MATERIAL_NONE; // a glow has no surface
  if ((v.metalness ?? 0) >= 0.5) return MATERIAL_METAL;
  const { h, s, l } = hsl(v.color);
  const rough = v.roughness ?? 0.5;
  // Pale and warm: bone (a skeleton, a tusk, a claw); a flushed face is more saturated than this.
  if (l > 0.7 && h >= 20 && h <= 70 && s > 0.04 && s <= 0.5) return MATERIAL_BONE;
  if (s < 0.2) {
    // Greys: stone when mid and matte, else cloth.
    if (rough >= 0.8 && l > 0.2 && l < 0.7) return MATERIAL_STONE;
    return MATERIAL_CLOTH;
  }
  // Skin tones: warm, moderately saturated, mid-light.
  if (h >= 8 && h <= 40 && s >= 0.25 && s <= 0.75 && l >= 0.4 && l <= 0.82) return MATERIAL_SKIN;
  // Earthy browns are leather.
  if (h >= 12 && h <= 45 && l < 0.4) return MATERIAL_LEATHER;
  // Greens (orcs, goblins, zombies, moss) read as hide.
  if (h >= 70 && h <= 170 && s >= 0.2) return MATERIAL_HIDE;
  return MATERIAL_CLOTH;
}
